/**
 * Intrinsic Daily — first-party website analytics tracker.
 *
 * Cookieless, privacy-friendly: stores an anonymous visitor id + a rolling
 * session id in localStorage and posts pageviews / conversion events to the
 * backend collector. No third parties, no personal data.
 */
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/collect`;

const VKEY = "id_vid";
const SKEY = "id_sid";
const STKEY = "id_sid_ts";
const SESSION_MS = 30 * 60 * 1000; // 30-min rolling session

// Don't track the private admin/console surfaces.
const EXCLUDE = ["/console-7h3x9q", "/admin", "/hq"];

const uuid = () => {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) {}
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const getVisitor = () => {
  let v = localStorage.getItem(VKEY);
  const isNew = !v;
  if (!v) {
    v = uuid();
    localStorage.setItem(VKEY, v);
  }
  return { visitor_id: v, is_new_visitor: isNew };
};

const getSession = () => {
  const now = Date.now();
  let s = localStorage.getItem(SKEY);
  const ts = Number(localStorage.getItem(STKEY) || 0);
  let isNew = false;
  if (!s || now - ts > SESSION_MS) {
    s = uuid();
    isNew = true;
  }
  localStorage.setItem(SKEY, s);
  localStorage.setItem(STKEY, String(now));
  return { session_id: s, is_new_session: isNew };
};

const send = (payload) => {
  try {
    if (EXCLUDE.some((p) => (payload.path || "").startsWith(p))) return;
    const visitor = getVisitor();
    const session = getSession();
    // Capture UTM campaign tags from the current URL (attribution).
    let utm = {};
    try {
      const q = new URLSearchParams(window.location.search);
      utm = {
        utm_source: q.get("utm_source") || "",
        utm_medium: q.get("utm_medium") || "",
        utm_campaign: q.get("utm_campaign") || "",
      };
    } catch (_) {}
    const body = JSON.stringify({
      ...payload,
      ...visitor,
      ...session,
      ...utm,
      referrer: document.referrer || "",
      language: navigator.language || "",
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(BASE, new Blob([body], { type: "application/json" }));
    } else {
      fetch(BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {
    /* never break the site because of analytics */
  }
};

export const trackPageview = (path) => {
  const p = path || window.location.pathname;
  const now = Date.now();
  // Guard against duplicate fires for the same path within a short window
  // (e.g. React StrictMode double-invoking effects in development).
  if (trackPageview._last && trackPageview._last.p === p && now - trackPageview._last.t < 1500) return;
  trackPageview._last = { p, t: now };
  send({ type: "pageview", path: p });
};

export const trackEvent = (event_name, path) =>
  send({ type: "event", event_name, path: path || window.location.pathname });
